import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLang } from '@/context/LanguageContext';
import { useTheme } from '@/context/ThemeContext';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { WhatsAppInput } from '@/components/WhatsAppInput';
import { Heart, ArrowLeft, Globe, Lock, Sun, Moon, Phone, CheckCircle2, AlertCircle, BookOpen, X } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { TutorialSections } from '@/pages/TutorialPage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TUTORIAL_SEEN_KEY = 'mg_tutorial_seen';

export default function AuthPage() {
  const navigate = useNavigate();
  const { t, lang, toggleLang } = useLang();
  const { theme, toggleTheme } = useTheme();
  const { loginMember } = useAuth();
  const [whatsapp, setWhatsapp] = useState('+62');
  const [passkey, setPasskey] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Onboarding tutorial: shown automatically on the first visit (per device).
  const [tutorialOpen, setTutorialOpen] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem(TUTORIAL_SEEN_KEY)) {
      setTutorialOpen(true);
    }
  }, []);

  const closeTutorial = () => {
    localStorage.setItem(TUTORIAL_SEEN_KEY, '1');
    setTutorialOpen(false);
  };

  // Normalised digits-only number (excluding the leading +); used to validate before opening the confirm dialog
  const rawDigits = whatsapp.replace(/^\+/, '').replace(/\D/g, '');
  const hasValidWa = rawDigits.length >= 6 && rawDigits.length <= 15 && whatsapp.startsWith('+');
  const canSubmit = hasValidWa && passkey.trim().length > 0;

  const openConfirm = () => {
    if (!hasValidWa) {
      toast.error(lang === 'en'
        ? 'Please enter a valid WhatsApp number (6 to 15 digits, no leading 0)'
        : 'Mohon masukkan nomor WhatsApp yang valid (6 sampai 15 digit, tanpa awalan 0)');
      return;
    }
    if (!passkey.trim()) {
      toast.error(lang === 'en' ? 'Please enter the site passkey' : 'Mohon masukkan site passkey');
      return;
    }
    setConfirmOpen(true);
  };

  const handleLogin = async () => {
    setConfirmOpen(false);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, {
        whatsapp: whatsapp.trim(),
        passkey: passkey.trim(),
      });
      loginMember(res.data.token, res.data.member);
      toast.success(lang === 'en' ? 'Welcome!' : 'Selamat datang!');
      navigate('/browse');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (lang === 'en' ? 'Login failed' : 'Gagal masuk'));
    }
    setLoading(false);
  };

  const onKey = (e) => { if (e.key === 'Enter') openConfirm(); };

  return (
    <div className="min-h-screen t-bg relative flex items-center justify-center">
      <div className="fixed top-0 left-0 right-0 z-50 px-6 py-4 flex items-center justify-between">
        <button onClick={() => navigate('/')} className="t-text-secondary hover:opacity-80 transition" data-testid="auth-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2">
          <button onClick={toggleTheme} data-testid="auth-theme-toggle" className="flex items-center gap-1 px-2.5 py-1.5 rounded-full glass text-sm t-text-secondary">
            {theme === 'dark' ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
          </button>
          <button onClick={toggleLang} data-testid="auth-lang-toggle" className="flex items-center gap-1 px-2.5 py-1.5 rounded-full glass text-sm t-text-secondary">
            <Globe className="w-3.5 h-3.5" />{lang === 'en' ? 'ID' : 'EN'}
          </button>
        </div>
      </div>

      <div className="relative z-10 w-full max-w-md mx-auto px-6 py-20">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
          <div className="w-16 h-16 rounded-2xl bg-white/80 flex items-center justify-center mx-auto mb-6 shadow-sm">
            <img src="/images/mgi-logo.jpeg" alt="MGI" className="w-12 h-12 object-contain" />
          </div>
          <h1 className="text-3xl font-bold t-text mb-2">{t('login')}</h1>
          <p className="t-text-muted font-body">Match Group Indonesia</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
          <div className="glass-card rounded-2xl p-6 sm:p-8 space-y-5">
            {/* WhatsApp */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Phone className="w-4 h-4 text-[#25D366]" />
                <span className="t-text-secondary text-sm font-medium">
                  {lang === 'en' ? 'WhatsApp Number' : 'Nomor WhatsApp'}
                </span>
              </div>
              <WhatsAppInput value={whatsapp} onChange={setWhatsapp} lang={lang} />
              {/* Extra emphasis on the "no leading 0" rule, beyond what WhatsAppInput already shows */}
              <div className="mt-2.5 flex items-start gap-2 px-3 py-2 rounded-lg bg-[#F59E0B]/8 border border-[#F59E0B]/25">
                <AlertCircle className="w-3.5 h-3.5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
                <p className="t-text text-[11px] leading-snug">
                  {lang === 'en'
                    ? <><b>Important:</b> Do NOT type the leading 0. Replace the 0 with your country code from the dropdown. Example: number <code className="px-1 rounded bg-black/10">0812-3456-7890</code> becomes <code className="px-1 rounded bg-black/10">+62 812 3456 7890</code>.</>
                    : <><b>Penting:</b> JANGAN tulis angka 0 di depan. Ganti 0 dengan kode negara dari dropdown. Contoh: nomor <code className="px-1 rounded bg-black/10">0812-3456-7890</code> menjadi <code className="px-1 rounded bg-black/10">+62 812 3456 7890</code>.</>}
                </p>
              </div>
            </div>

            {/* Passkey */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Lock className="w-4 h-4 text-[#C7867A]" />
                <span className="t-text-secondary text-sm font-medium">{t('passkey')}</span>
              </div>
              <Input
                data-testid="passkey-input"
                type="password"
                placeholder={t('passkey_placeholder')}
                value={passkey}
                onChange={(e) => setPasskey(e.target.value)}
                className="t-bg-input t-border h-12 rounded-xl text-base px-4 t-text"
                onKeyDown={onKey}
                autoComplete="current-password"
                maxLength={100}
              />
              <p className="t-text-muted text-[11px] mt-2">
                {lang === 'en'
                  ? 'The passkey is set by the admin and rotated monthly. Contact the admin if you do not have the current passkey.'
                  : 'Passkey diatur oleh admin dan dirotasi setiap bulan. Hubungi admin jika Anda belum memiliki passkey terbaru.'}
              </p>
            </div>
          </div>

          <Button
            data-testid="login-btn"
            onClick={openConfirm}
            disabled={loading || !canSubmit}
            className="w-full bg-[#D98A94] hover:bg-[#D98A94]/90 text-white rounded-full h-14 text-lg font-medium btn-glow active:scale-95 disabled:opacity-50"
          >
            <Heart className="w-5 h-5 mr-2" />
            {loading ? '...' : t('login')}
          </Button>

          {/* Tutorial link */}
          <button
            onClick={() => navigate('/tutorial')}
            data-testid="tutorial-link"
            className="w-full text-center t-text-secondary hover:t-text text-sm py-2 transition underline underline-offset-4 decoration-dotted"
          >
            {lang === 'en' ? 'New here? Read the step-by-step tutorial →' : 'Pertama kali? Baca tutorial lengkap →'}
          </button>
        </motion.div>
      </div>

      {/* Confirmation dialog — verify WhatsApp number before submit */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="glass-card max-w-md t-text" data-testid="wa-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="t-text flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-[#25D366]" />
              {lang === 'en' ? 'Confirm your WhatsApp number' : 'Konfirmasi nomor WhatsApp Anda'}
            </DialogTitle>
            <DialogDescription className="t-text-muted text-xs">
              {lang === 'en'
                ? 'Please verify the number below is correct. This is the number you used.'
                : 'Mohon pastikan nomor di bawah ini sudah benar. Ini adalah nomor yang Anda gunakan.'}
            </DialogDescription>
          </DialogHeader>
          <div className="my-4 p-5 rounded-2xl border-2 border-[#25D366]/30 bg-[#25D366]/5 text-center">
            <p className="t-text-muted text-[10px] uppercase tracking-wider mb-2">{lang === 'en' ? 'WhatsApp number' : 'Nomor WhatsApp'}</p>
            <p data-testid="confirm-wa-display" className="t-text text-2xl sm:text-3xl font-bold tracking-wide">{whatsapp}</p>
          </div>
          <div className="flex gap-3">
            <Button onClick={() => setConfirmOpen(false)} variant="outline" className="flex-1 rounded-full h-11 t-border t-text-secondary" data-testid="confirm-edit-btn">
              {lang === 'en' ? 'Edit number' : 'Edit nomor'}
            </Button>
            <Button onClick={handleLogin} disabled={loading} className="flex-1 rounded-full h-11 bg-[#D98A94] hover:bg-[#D98A94]/90 text-white" data-testid="confirm-login-btn">
              {loading ? '...' : (lang === 'en' ? 'Yes, log me in' : 'Ya, masuk')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Onboarding tutorial: auto-opens on first visit. User reads through ALL sections (forced open),
          then taps the close button to dismiss and start logging in. Remembered per-device. */}
      <Dialog open={tutorialOpen} onOpenChange={(open) => { if (!open) closeTutorial(); }}>
        <DialogContent
          className="glass-card t-text w-[calc(100vw-1rem)] max-w-2xl h-[90vh] overflow-y-auto p-4 sm:p-6 rounded-2xl"
          data-testid="onboarding-tutorial-dialog"
        >
          <DialogHeader>
            <DialogTitle className="t-text flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#D98A94]/10 text-[#D98A94] flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <p className="text-xs t-text-muted uppercase tracking-wider font-semibold">
                  {lang === 'en' ? 'Quick Tutorial' : 'Tutorial Singkat'}
                </p>
                <p className="t-text text-lg font-bold leading-tight">
                  {lang === 'en' ? 'How to use Match Group Indonesia' : 'Cara menggunakan Match Group Indonesia'}
                </p>
              </div>
            </DialogTitle>
            <DialogDescription className="t-text-muted text-xs mt-2">
              {lang === 'en'
                ? 'Please scroll through every section below. When you are done, close this window to continue logging in.'
                : 'Silakan baca seluruh bagian di bawah ini. Setelah selesai, tutup jendela ini untuk lanjut login.'}
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4">
            <TutorialSections forceOpen />
          </div>

          <div className="sticky bottom-0 -mx-4 sm:-mx-6 -mb-4 sm:-mb-6 mt-4 px-4 sm:px-6 py-3 border-t backdrop-blur-md"
               style={{ borderColor: 'var(--border-color)', backgroundColor: 'rgba(var(--bg-rgb,0,0,0),0.85)' }}>
            <Button
              onClick={closeTutorial}
              data-testid="onboarding-tutorial-close-btn"
              className="w-full h-12 rounded-full bg-[#D98A94] hover:bg-[#D98A94]/90 text-white font-medium"
            >
              <X className="w-4 h-4 mr-2" />
              {lang === 'en' ? 'Close & Continue to Login' : 'Tutup & Lanjut Login'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
